// ============================================================================
// LE GLISSER-DÉPOSER DE TUILES — un seul patron pour tout réordonnancement.
//
// UTILITÉ : articles rapides, photos de fiche article, lignes du popover « Colonnes », et tout
// réordonnancement à venir. Charte §4. Écrire trois recettes de glisser, c'était garantir que la
// troisième n'aurait ni l'annulation au clavier ni le seuil de saisie des deux premières.
//
// ⚠️ CE MODULE EST PUR : des nombres et des rôles, aucun DOM, aucun React. La mécanique vit dans
// `@orgazone/ui-web/tile-drag` ; ici on ne fixe que ce qui se règle.
// ============================================================================
import { Z_LAYERS } from './z';

/**
 * Les réglages du geste.
 *
 * ⚠️ `threshold` EXISTE POUR QUE LE CLIC RESTE UN CLIC : sans seuil, le moindre tremblement de la main
 * pendant un clic saisit la tuile, et une case à cocher devient impossible à cocher. En dessous de
 * 5 px, c'est un clic — et l'élément reçoit son `click` normalement.
 *
 * ⚠️ `flipMs` ANIME LES VOISINES PENDANT LE SURVOL, jamais au dépôt : le réordonnancement doit être
 * visible EN CONTINU. Un déplacement qui ne se voit qu'au relâchement est un « drop surprise » —
 * l'utilisateur lâche sans savoir où la tuile va tomber.
 *
 * ⚠️ `ghost.zIndex` VIENT DE `Z_LAYERS.dragGhost` (1050) ET NON DU 1200 DE LA CHARTE. Divergence
 * ASSUMÉE et mesurée : 1200 est ici le plan du TOAST, et un fantôme posé dessus passerait au-dessus
 * des messages système — y compris celui qui annoncerait l'échec de l'enregistrement qu'on est en
 * train de provoquer.
 */
export const TILE_DRAG = {
  /** Mouvement (px) avant que le geste devienne une saisie. En dessous : c'est un clic. */
  threshold: 5,
  /** Le clone qui suit le curseur — `position:fixed`, `pointer-events:none`. */
  ghost: { scale: 1.04, zIndex: Z_LAYERS.dragGhost, opacity: 0.96 },
  /** La marque laissée à la place d'origine — MÊMES dimensions que la tuile, jamais un vide. */
  placeholder: { borderWidth: 1.5, borderStyle: 'dashed', radius: 12 },
  /** Durée du glissement des voisines vers leur nouvelle place (FLIP). */
  flipMs: 150,
  /**
   * LE GESTE « EN PLACE » (studio des articles rapides) : la tuile saisie RESTE à sa place, estompée — c'est elle que
   * les voisines contournent — au lieu de céder la place à un creux ; le fantôme, incliné, la rejoint au lâcher puis
   * s'efface. On voit ce qu'on tient, et où il tombe.
   */
  inPlace: { faded: 0.3, fadeMs: 150, ghostOpacity: 0.85, ghostRotate: -1, landingMs: 220 },
} as const;

/**
 * Les styles de repos OBLIGATOIRES sur toute tuile saisissable.
 *
 * ⚠️ DÈS LE REPOS, PAS À LA SAISIE (bug corrigé le 2026-08-12, il ne doit jamais revenir) : sans
 * `user-select:none`, le premier mouvement sélectionne le texte de la tuile au lieu de la saisir, et
 * le navigateur affiche une surbrillance bleue par-dessus le geste. Sans `touch-action:none`, le
 * doigt fait défiler la page — la tuile ne bouge jamais sur tablette.
 */
export const TILE_DRAG_REST = {
  userSelect: 'none',
  WebkitUserSelect: 'none',
  touchAction: 'none',
} as const;
